import { Injectable } from '@angular/core';
import {
	AttestationPresenceThematique,
	AttestationPresenceThematiqueInterface,
	AttestationPresenceThematiquePayload,
} from './attestation-presence-thematique';
import { AttestationPresenceThematiqueSearchParamsInterface } from './attestation-presence-thematique-search-params';
import { BaseModelService } from '@app/abstracts';
import { Helpers } from '@app/shared/helpers';
@Injectable()
export class AttestationPresenceThematiqueService extends BaseModelService<
	AttestationPresenceThematique,
	AttestationPresenceThematiqueInterface,
	AttestationPresenceThematiquePayload,
	AttestationPresenceThematiqueSearchParamsInterface
> {
	/** @inheritDoc */
	protected path(): string {
		return 'attestation-presence-thematique';
	}
	/** @inheritDoc */
	protected new(
		object: AttestationPresenceThematiqueInterface
	): AttestationPresenceThematique {
		return new AttestationPresenceThematique(object);
	}
	/** @inheritDoc */
	protected transformSearchParams(
		searchParams: AttestationPresenceThematiqueSearchParamsInterface
	): AttestationPresenceThematiqueSearchParamsInterface {
		// Search by edge for Date
		if (typeof searchParams.date__min !== 'undefined') {
			searchParams.date__min = Helpers.toMidnightUTC(
				searchParams.date__min
			);
		}
		if (typeof searchParams.date__max !== 'undefined') {
			searchParams.date__max = Helpers.toLastSecondUTC(
				searchParams.date__max
			);
		}
		if (typeof searchParams.date !== 'undefined') {
			searchParams.date__min = Helpers.toMidnightUTC(searchParams.date);
			searchParams.date__max = Helpers.toLastSecondUTC(searchParams.date);
			delete searchParams.date;
		}
		return searchParams;
	}

	/**
	 * Sign presence for a thematique session
	 * @param thematiqueId - Formation thematique ID
	 * @param date - Signature date as timestamp (number)
	 * @param period - Period ('morning' or 'afternoon')
	 * @param signature - Base64 signature image
	 */
	signPresence(
		thematiqueId: string,
		date: number,
		period: 'morning' | 'afternoon',
		signature: string,
		asFormateur = false
	): Promise<any> {
		const options = {
			withCredentials: !this.publicRead,
		};
		const payload = {
			signature,
			thematiqueId,
			date,
			period,
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		};
		return this.http
			.post<any>(
				asFormateur
					? `${this.uri()}/sign-formateur`
					: `${this.uri()}/sign`,
				payload,
				options
			)
			.toPromise();
	}

	/**
	 * Sign presence as formateur for a stagiaire
	 * @param thematiqueId - Formation thematique ID
	 * @param date - Signature date as timestamp (number)
	 * @param period - Period ('morning' or 'afternoon')
	 * @param stagiaireId - Stagiaire user ID
	 * @param delay - Delay in minutes (optional)
	 */
	signPresenceAsFormateur(
		thematiqueId: string,
		date: number,
		period: 'morning' | 'afternoon',
		signature: string
	): Promise<any> {
		return this.signPresence(thematiqueId, date, period, signature, true);
	}

	/**
	 * Sign presence as formateur for a stagiaire
	 * @param thematiqueId - Formation thematique ID
	 * @param date - Signature date as timestamp (number)
	 * @param period - Period ('morning' or 'afternoon')
	 * @param stagiaireId - Stagiaire user ID
	 * @param delay - Delay in minutes (optional)
	 * @param asAdmin - Whether to use admin endpoint
	 */
	signDelay(
		thematiqueId: string,
		date: number,
		period: 'morning' | 'afternoon',
		stagiaireId: string,
		delay?: number,
		asAdmin = false
	): Promise<any> {
		const options = {
			withCredentials: true,
		};
		const payload = {
			stagiaireId,
			thematiqueId,
			date,
			period,
			delay,
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		};
		return this.http
			.post<any>(`${this.uri(asAdmin)}/sign-delay`, payload, options)
			.toPromise();
	}

	/**
	 * Sign presence as admin for a stagiaire
	 */
	signDelayAsAdmin(
		thematiqueId: string,
		date: number,
		period: 'morning' | 'afternoon',
		stagiaireId: string,
		delay?: number
	): Promise<any> {
		return this.signDelay(
			thematiqueId,
			date,
			period,
			stagiaireId,
			delay,
			true
		);
	}

	/**
	 * Mark a stagiaire as excused for a thematique slot. Upserts/toggles on the API side:
	 * 201 = created, 200 = upgraded existing or toggled off (body.deleted = true), 409 = already signed.
	 */
	markExcused(
		thematiqueId: string,
		date: number,
		period: 'morning' | 'afternoon',
		stagiaireId: string,
		asAdmin = false
	): Promise<any> {
		const options = {
			withCredentials: true,
		};
		const payload = {
			stagiaireId,
			thematiqueId,
			date,
			period,
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		};
		return this.http
			.post<any>(`${this.uri(asAdmin)}/mark-excused`, payload, options)
			.toPromise();
	}

	/**
	 * Admin variant — no 48h window check on the server.
	 */
	markExcusedAsAdmin(
		thematiqueId: string,
		date: number,
		period: 'morning' | 'afternoon',
		stagiaireId: string
	): Promise<any> {
		return this.markExcused(thematiqueId, date, period, stagiaireId, true);
	}
}
